import React, { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import { applyBrandTheme } from '../../../constants/qcsTheme'
import { setBrowserTabTitle } from '../../../helpers/setBrowserTabTitle'
import QcsLoaderLogo from '../../../uiKit/icons/QcsLoaderLogo'

const DOT_COUNT = 5
const DOT_STEP_MS = 300
const DOT_INDEXES = Array.from({ length: DOT_COUNT }, (_, index) => index)

// JSS 9 can't scope keyframes, so they are registered globally under a unique prefix.
const dotKeyframes = DOT_INDEXES.reduce((acc, index) => ({
  ...acc,
  [`@keyframes qcsHandoffDot${index}`]: {
    '0%': { opacity: index === 0 ? 1 : 0.5 },
    [`${(index * 100) / DOT_COUNT}%`]: { opacity: 1 },
    '100%': { opacity: 1 },
  },
}), {})

const dotAnimations = DOT_INDEXES.reduce((acc, index) => ({
  ...acc,
  [`dot${index}`]: {
    animation: `qcsHandoffDot${index} ${DOT_COUNT * DOT_STEP_MS}ms step-end infinite`,
  },
}), {})

const styles = {
  '@global': dotKeyframes,
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7FA',
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logo: {
    display: 'block',
    flexShrink: 0,
  },
  dots: {
    display: 'flex',
    marginTop: 27.4,
  },
  dot: {
    width: 17.5,
    height: 17.5,
    borderRadius: '50%',
    backgroundColor: '#0F4146',
    opacity: 0.5,
    '& + &': { marginLeft: 15.3 },
  },
  ...dotAnimations,
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}

/** Shown while the QCS handoff id is exchanged for a session, instead of the login layout. */
const QcsHandoffLoadingPage = ({ classes }) => {
  // Layout effect: the unmount cleanup must run before QcsHome's mount re-applies the theme.
  useLayoutEffect(() => {
    setBrowserTabTitle('QCS')
    applyBrandTheme(true)
    return () => applyBrandTheme(false)
  }, [])

  return (
    <main className={classes.page}>
      <section className={classes.loader} role="status" aria-live="polite">
        <QcsLoaderLogo className={classes.logo} width={174} height={120} />
        <div className={classes.dots} aria-hidden="true">
          {DOT_INDEXES.map(index => (
            <span key={index} className={`${classes.dot} ${classes[`dot${index}`]}`} />
          ))}
        </div>
        <span className={classes.visuallyHidden}>Signing you in…</span>
      </section>
    </main>
  )
}

QcsHandoffLoadingPage.propTypes = {
  classes: PropTypes.object.isRequired,
}

export default withStyles(styles)(QcsHandoffLoadingPage)
